// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import Page from 'main-app/components/Page';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import {
  PrintWrapper,
  PrintOnly,
  NoPrint,
  PrintPage,
  PrintDocHeader,
  PrintDocHeaderTitle,
  PrintDocHeaderCompany,
  PrintInfoGrid,
  PrintInfoColumn,
  PrintInfoRow,
  PrintInfoLabel,
  PrintNotes,
  PrintItemTable,
  PrintDocFooter,
  PrintSignatureGrid,
  PrintSignatureColumn,
  PrintSignatureLine,
  PrintSignatureUnderline,
} from 'shared/components/common/PrintLayout';
import UpdatePurchaseOrderStatusMutation from 'main-app/mutations/UpdatePurchaseOrderStatus';
import ApprovePurchaseOrderMutation from 'main-app/mutations/ApprovePurchaseOrder';
import EmailPurchaseOrderMutation from 'main-app/mutations/EmailPurchaseOrder';
import * as Actions from 'main-app/store/Actions';
import moment from 'moment';

type Props = {
  history: RouterHistory,
  match: {
    params: {
      id: string,
    },
  },
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 1200px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const LeftHeader = styled.div`
  flex: 1;
`;

const PONumber = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.status) {
      case 'PENDING_APPROVAL':
        return '#fff3cd';
      case 'APPROVED':
        return '#cfe2ff';
      case 'SENT':
        return '#e2d9f3';
      case 'PARTIALLY_RECEIVED':
        return '#ffe4b5';
      case 'RECEIVED':
        return '#d1f2dd';
      case 'CANCELLED':
      case 'REJECTED':
        return '#f8d7da';
      default:
        return '#e0e0e0';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'PENDING_APPROVAL':
        return '#856404';
      case 'APPROVED':
        return '#084298';
      case 'SENT':
        return '#6f42c1';
      case 'PARTIALLY_RECEIVED':
        return '#cc5500';
      case 'RECEIVED':
        return '#0f5132';
      case 'CANCELLED':
      case 'REJECTED':
        return '#842029';
      default:
        return '#666';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BackButton = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const InfoSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #1a1a1a;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;
`;

const LineItemsSection = styled.div`
  margin-bottom: 32px;
`;

const NotesSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const NotesText = styled.p`
  margin: 8px 0 0 0;
  color: #666;
  line-height: 1.6;
`;

class PurchaseOrderDetail extends PureComponent<Props> {
  handleApprove = async (poId: string) => {
    Actions.alertNotification(
      i18n.t('Approving purchase order...'),
      'info'
    );

    try {
      const response = await ApprovePurchaseOrderMutation.commit({
        variables: {
          input: {
            id: poId,
            status: 'APPROVED',
          },
        },
      });

      if (response.updatePurchaseOrderStatus?.purchaseOrder) {
        Actions.alertNotification(
          i18n.t('Purchase Order Approved'),
          'success'
        );
        window.location.reload();
      } else {
        Actions.alertNotification(
          i18n.t('Failed to approve purchase order'),
          'error'
        );
      }
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  handleMarkAsSent = async (poId: string) => {
    try {
      await UpdatePurchaseOrderStatusMutation.commit({
        variables: {
          input: {
            id: poId,
            status: 'SENT',
          },
        },
      });

      Actions.alertNotification(
        i18n.t('Purchase Order marked as Sent'),
        'success'
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  handleCancel = async (poId: string) => {
    const confirmed = window.confirm(
      i18n.t('Are you sure you want to cancel this purchase order?')
    );

    if (!confirmed) return;

    try {
      await UpdatePurchaseOrderStatusMutation.commit({
        variables: {
          input: {
            id: poId,
            status: 'CANCELLED',
          },
        },
      });

      Actions.alertNotification(
        i18n.t('Purchase Order Cancelled'),
        'success'
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  formatCurrency = (amount: ?number) => {
    if (!amount) return `${getCurrencySymbol()}0.00`;
    return `${getCurrencySymbol()}${parseFloat(amount).toFixed(2)}`;
  };

  formatDate = (date: ?string) => {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  };

  handlePrintPO = () => {
    window.print();
  };

  handleEmailPurchaseOrder = (purchaseOrderId: string) => {
    EmailPurchaseOrderMutation.commit({
      variables: { input: { purchaseOrderId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Purchase order email sent successfully.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error sending purchase order email.'), 'error');
      });
  };

  render() {
    const { match, history } = this.props;
    const { id } = match.params;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query PurchaseOrderDetailQuery($id: ID!) {
            purchaseOrder(id: $id) {
              id
              poNumber
              vendor {
                id
                vendorNumber
                name
                email
                phone
                address
                city
                state
                zipCode
                country
              }
              status
              totalAmount
              expectedDeliveryDate
              receivedAt
              notes
              requestedBy {
                id
                firstName
                lastName
              }
              approvedBy {
                id
                firstName
                lastName
              }
              approvedAt
              createdAt
              updatedAt
              lineItems {
                id
                item {
                  id
                  itemNumber
                  name
                }
                quantity
                unitPrice
                totalPrice
                quantityReceived
              }
            }
            viewer {
              id
              company {
                id
                name
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
            }
          }
        `}
        variables={{ id }}
        render={({ props }) => {
          if (!props || !props.purchaseOrder) {
            return <Loader />;
          }

          const po = props.purchaseOrder;
          const viewer = props.viewer;

          return (
            <Page>
              <PrintWrapper>
              <PrintOnly>
                <PrintPage>
                  <PrintDocHeader>
                    <PrintDocHeaderTitle>
                      <Logo style={{ width: 100, height: 'auto', display: 'block', marginBottom: 4 }} />
                      {i18n.t('Purchase Order')}
                    </PrintDocHeaderTitle>
                    <PrintDocHeaderCompany>{viewer && viewer.company && viewer.company.name}</PrintDocHeaderCompany>
                  </PrintDocHeader>

                  <PrintInfoGrid>
                    <PrintInfoColumn>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('PO #')}</PrintInfoLabel>
                        <div>{po.poNumber}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Date')}</PrintInfoLabel>
                        <div>{this.formatDate(po.createdAt)}</div>
                      </PrintInfoRow>
                      {po.expectedDeliveryDate && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Expected Delivery')}</PrintInfoLabel>
                          <div>{this.formatDate(po.expectedDeliveryDate)}</div>
                        </PrintInfoRow>
                      )}
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Vendor')}</PrintInfoLabel>
                        <div>
                          <div>{po.vendor.name}</div>
                          {po.vendor.vendorNumber && <div>#{po.vendor.vendorNumber}</div>}
                        </div>
                      </PrintInfoRow>
                      {(po.vendor.address || po.vendor.city) && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Vendor Address')}</PrintInfoLabel>
                          <div>
                            {po.vendor.address && <div>{po.vendor.address}</div>}
                            {(po.vendor.city || po.vendor.state || po.vendor.zipCode) && (
                              <div>{[po.vendor.city, po.vendor.state, po.vendor.zipCode].filter(Boolean).join(', ')}</div>
                            )}
                            {po.vendor.country && <div>{po.vendor.country}</div>}
                          </div>
                        </PrintInfoRow>
                      )}
                      {po.vendor.email && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Email')}</PrintInfoLabel>
                          <div>{po.vendor.email}</div>
                        </PrintInfoRow>
                      )}
                      {po.vendor.phone && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                          <div>{po.vendor.phone}</div>
                        </PrintInfoRow>
                      )}
                      {po.requestedBy && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Requested By')}</PrintInfoLabel>
                          <div>{po.requestedBy.firstName} {po.requestedBy.lastName}</div>
                        </PrintInfoRow>
                      )}
                    </PrintInfoColumn>
                    {viewer && viewer.company && (
                      <PrintInfoColumn>
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Buyer')}</PrintInfoLabel>
                          <div>{viewer.company.name}</div>
                        </PrintInfoRow>
                        {(viewer.company.addressLine1 || viewer.company.addressCity) && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Address')}</PrintInfoLabel>
                            <div>
                              {viewer.company.addressLine1 && <div>{viewer.company.addressLine1}</div>}
                              {viewer.company.addressLine2 && <div>{viewer.company.addressLine2}</div>}
                              {(viewer.company.addressCity || viewer.company.addressState || viewer.company.addressZip) && (
                                <div>{[viewer.company.addressCity, viewer.company.addressState, viewer.company.addressZip].filter(Boolean).join(', ')}</div>
                              )}
                              {viewer.company.addressCountry && <div>{viewer.company.addressCountry}</div>}
                            </div>
                          </PrintInfoRow>
                        )}
                        {viewer.company.phone && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                            <div>{viewer.company.phone}</div>
                          </PrintInfoRow>
                        )}
                      </PrintInfoColumn>
                    )}
                  </PrintInfoGrid>

                  <PrintItemTable>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Item #')}</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Description')}</th>
                        <th>{i18n.t('Quantity')}</th>
                        <th style={{ textAlign: 'right' }}>{i18n.t('Unit Price')}</th>
                        <th style={{ textAlign: 'right' }}>{i18n.t('Total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(po.lineItems || []).map((line, idx) => (
                        <tr key={line.id}>
                          <td>{idx + 1}</td>
                          <td>{line.item.itemNumber || '-'}</td>
                          <td>{line.item.name}</td>
                          <td style={{ textAlign: 'center' }}>{parseFloat(line.quantity).toFixed(2)}</td>
                          <td style={{ textAlign: 'right' }}>{this.formatCurrency(line.unitPrice)}</td>
                          <td style={{ textAlign: 'right' }}>{this.formatCurrency(line.totalPrice)}</td>
                        </tr>
                      ))}
                      {(!po.lineItems || po.lineItems.length === 0) && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{i18n.t('No items')}</td>
                        </tr>
                      )}
                    </tbody>
                  </PrintItemTable>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '16px 0 24px' }}>
                    <table style={{ borderSpacing: 0, minWidth: 280 }}>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 700, padding: '8px 16px 8px 0', textAlign: 'right', borderTop: '2px solid #333' }}>{i18n.t('Total:')}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', borderTop: '2px solid #333', fontWeight: 700 }}>{this.formatCurrency(po.totalAmount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {po.notes && (
                    <PrintNotes>
                      <PrintInfoLabel>{i18n.t('Notes')}</PrintInfoLabel>
                      <div>{po.notes}</div>
                    </PrintNotes>
                  )}

                  <PrintSignatureGrid>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('Approved by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('Vendor signature:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                  </PrintSignatureGrid>

                  <PrintDocFooter>
                    <span>{i18n.t('Created')} {this.formatDate(po.createdAt)}</span>
                  </PrintDocFooter>
                </PrintPage>
              </PrintOnly>

              <NoPrint>
              <Wrapper>
              <BackButton onClick={() => history.push('/purchase-orders')}>
                <Icon type="circle-arrow-left" size={20} />
              </BackButton>
              <HeaderSection>
                <LeftHeader>
                  <PONumber>{po.poNumber}</PONumber>
                  <StatusBadge status={po.status}>
                    {po.status.replace(/_/g, ' ')}
                  </StatusBadge>
                </LeftHeader>
                <ActionButtons>
                  {po.status === 'PENDING_APPROVAL' && (
                    <Button
                      theme="blue"
                      onClick={() => this.handleApprove(po.id)}
                    >
                      {i18n.t('Approve')}
                    </Button>
                  )}
                  {po.status === 'APPROVED' && (
                    <Button
                      theme="blue"
                      onClick={() => this.handleMarkAsSent(po.id)}
                    >
                      {i18n.t('Mark as Sent')}
                    </Button>
                  )}
                  {(po.status === 'SENT' ||
                    po.status === 'PARTIALLY_RECEIVED') && (
                    <Button
                      theme="blue"
                      onClick={() =>
                        history.push(`/purchase-order/${po.id}/receive`)
                      }
                    >
                      {i18n.t('Receive Items')}
                    </Button>
                  )}
                  {po.status !== 'CANCELLED' && po.status !== 'RECEIVED' && (
                    <Button
                      theme="border-red"
                      onClick={() => this.handleCancel(po.id)}
                    >
                      {i18n.t('Cancel')}
                    </Button>
                  )}
                  <Button
                    theme="grey"
                    onClick={this.handlePrintPO}
                  >
                    {i18n.t('Print PO')}
                  </Button>
                  <Button
                    theme="grey"
                    onClick={() => this.handleEmailPurchaseOrder(po.id)}
                  >
                    {i18n.t('Email PO')}
                  </Button>
                </ActionButtons>
              </HeaderSection>

              <InfoSection>
                <SectionTitle>{i18n.t('Vendor Information')}</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Vendor Number')}</InfoLabel>
                    <InfoValue>{po.vendor.vendorNumber || '-'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Vendor Name')}</InfoLabel>
                    <InfoValue>{po.vendor.name}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Email')}</InfoLabel>
                    <InfoValue>{po.vendor.email || '-'}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Phone')}</InfoLabel>
                    <InfoValue>{po.vendor.phone || '-'}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </InfoSection>

              <InfoSection>
                <SectionTitle>{i18n.t('Purchase Order Details')}</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Total Amount')}</InfoLabel>
                    <InfoValue>{this.formatCurrency(po.totalAmount)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Expected Delivery')}</InfoLabel>
                    <InfoValue>
                      {this.formatDate(po.expectedDeliveryDate)}
                    </InfoValue>
                  </InfoItem>
                  {po.receivedAt && (
                    <InfoItem>
                      <InfoLabel>{i18n.t('Received At')}</InfoLabel>
                      <InfoValue>
                        {this.formatDate(po.receivedAt)}
                      </InfoValue>
                    </InfoItem>
                  )}
                  <InfoItem>
                    <InfoLabel>{i18n.t('Requested By')}</InfoLabel>
                    <InfoValue>
                      {po.requestedBy
                        ? `${po.requestedBy.firstName} ${po.requestedBy.lastName}`
                        : '-'}
                    </InfoValue>
                  </InfoItem>
                  {po.approvedBy && (
                    <>
                      <InfoItem>
                        <InfoLabel>{i18n.t('Approved By')}</InfoLabel>
                        <InfoValue>
                          {`${po.approvedBy.firstName} ${po.approvedBy.lastName}`}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>{i18n.t('Approved At')}</InfoLabel>
                        <InfoValue>
                          {this.formatDate(po.approvedAt)}
                        </InfoValue>
                      </InfoItem>
                    </>
                  )}
                  <InfoItem>
                    <InfoLabel>{i18n.t('Created At')}</InfoLabel>
                    <InfoValue>{this.formatDate(po.createdAt)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{i18n.t('Updated At')}</InfoLabel>
                    <InfoValue>{this.formatDate(po.updatedAt)}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </InfoSection>

              {po.notes && (
                <NotesSection>
                  <SectionTitle>{i18n.t('Notes')}</SectionTitle>
                  <NotesText>{po.notes}</NotesText>
                </NotesSection>
              )}

              <LineItemsSection>
                <SectionTitle>{i18n.t('Line Items')}</SectionTitle>
                <Table
                  data={po.lineItems}
                  columns={[
                    {
                      Header: i18n.t('Item Number'),
                      accessor: 'item.itemNumber',
                      width: 140,
                      Cell: ({ value }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {value}
                        </div>
                      ),
                    },
                    {
                      Header: i18n.t('Item Name'),
                      accessor: 'item.name',
                      width: 200,
                      Cell: ({ value }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {value}
                        </div>
                      ),
                    },
                    {
                      Header: i18n.t('Quantity'),
                      accessor: 'quantity',
                      width: 100,
                      Cell: ({ value }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {parseFloat(value).toFixed(2)}
                        </div>
                      ),
                    },
                    {
                      Header: i18n.t('Unit Price'),
                      accessor: 'unitPrice',
                      width: 120,
                      Cell: ({ value }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {this.formatCurrency(value)}
                        </div>
                      ),
                    },
                    {
                      Header: i18n.t('Total Price'),
                      accessor: 'totalPrice',
                      width: 120,
                      Cell: ({ value }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {this.formatCurrency(value)}
                        </div>
                      ),
                    },
                    {
                      Header: i18n.t('Qty Received'),
                      accessor: 'quantityReceived',
                      width: 120,
                      Cell: ({ value, original }) => (
                        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                          {parseFloat(value || 0).toFixed(2)} /{' '}
                          {parseFloat(original.quantity).toFixed(2)}
                        </div>
                      ),
                    },
                  ]}
                />
              </LineItemsSection>
            </Wrapper>
            </NoPrint>
            </PrintWrapper>
            </Page>
          );
        }}
      />
    );
  }
}

export default PurchaseOrderDetail;
